import React, { useState, useEffect, useRef } from 'react'

import { usePageEditorContext } from '../../contexts/page-editor.context'
import { ReportBlock } from '../../blocks/report-block'
import { selectComponentService } from '../../../common/services/select-component.service'
import { reportDataSetService } from '../report-data-set.service'
import { DatasetFieldsEditor, DatasetEmptyState } from '../dataset-fields-editor'
import { SUBGROUP_COLUMN } from '../report-data-set.constants'
import { BlockEditButtonGroup } from '../block-edit-button-group'

const dataFields = [
  // key, label, isRequired
  ['xField', 'X Field', true],
  ['yField', 'Y Field', true],
  ['yLoField', 'Y Field CI Low', false],
  ['yHiField', 'Y Field CI High', false],
  ['y2Field', 'Secondary Y Field', false]
]

const subgroupColumns = [SUBGROUP_COLUMN.STRATA, SUBGROUP_COLUMN.SLIDER, SUBGROUP_COLUMN.PAGE_FILTER, SUBGROUP_COLUMN.PRIMARY, SUBGROUP_COLUMN.FILTERS]

export const LineBlockEditInline = function () {
  const pageEditorContext = usePageEditorContext() || {}
  const currentEdit = pageEditorContext.currentEdit || {}
  const block = currentEdit.block || {}
  block.props = block.props || {}
  block.props.dataDefinition = block.props.dataDefinition || {}
  const dataDefinition = block.props.dataDefinition

  const dataSetOptions = useRef(reportDataSetService.getDataSetOptions(pageEditorContext.page, 'line'))
  const [editMode, setEditMode] = useState(true)
  const [dataKeyStatus, setDataKeyStatus] = useState(getDataKeyStatus())
  const [dataFieldOptions, setDataFieldOptions] = useState(getDataFieldOptions())

  useEffect(() => {
    // Disable the Save button if the required data fields are not populated
    if (!areRequiredFieldsPopulated()) {
      pageEditorContext.updateCurrentEdit({ isValid: false })
    }
  }, [])

  useEffect(() => {
    const dataKeyStatus = getDataKeyStatus()

    if (dataKeyStatus.isValid) {
      setDataFieldOptions(getDataFieldOptions())
    }
    setDataKeyStatus(dataKeyStatus)
  }, [block.dataKey, block.props.parentControlBar, block.props.parentBlock])

  function updateCurrentEdit (currentEdit) {
    // Ensure any updates to the block result in a new object
    if (currentEdit.block) {
      currentEdit.block = { ...currentEdit.block }
    }

    // Enables/Disables the Save button
    currentEdit.isValid = areRequiredFieldsPopulated()

    pageEditorContext.registerUnsavedChanges()
    pageEditorContext.updateCurrentEdit(currentEdit)
  }

  function getDataKeyStatus () {
    return {
      isSelected: block.dataKey !== undefined,
      isValid: selectComponentService.isKnownField(dataSetOptions.current, block.dataKey)
    }
  }

  function areRequiredFieldsPopulated () {
    return !dataFields.find(([key, label, isRequired]) => {
      const value = dataDefinition[key]
      return isRequired && (value === undefined || value === null)
    })
  }

  function getDataFieldOptions () {
    return reportDataSetService.getDataFieldOptions(pageEditorContext.page, block.dataKey)
  }

  function handleDataSetChange ({ value }) {
    // Clear out data field values
    dataFields.forEach(([key]) => {
      handleDataFieldChange(key, undefined)
    })

    if (block.dataKey === undefined) {
      setBlockDefaults()
    }

    block.dataKey = value
    updateCurrentEdit({ block })
  }

  function setBlockDefaults () {
    delete block.props.placeholder
    block.props.isTimeSeries = true
    block.props.timeFormat = 'MMM YYYY'
    block.props.xFormat = 'date'
    block.props.yFormat = 'none'
  }

  function handleDataFieldChange () {
    updateCurrentEdit({ block })
  }

  if (!dataKeyStatus.isSelected) {
    return <DatasetEmptyState block={block} dataSetOptions={dataSetOptions.current} isDatasetKeyValid={dataKeyStatus.isValid} handleDataSetChange={handleDataSetChange} />
  }

  return (
    <div className="report-editor-inline">
      <div className={editMode ? 'button-row-edit-mode' : 'button-row'}>
        {block.dataKey !== undefined && (
          <BlockEditButtonGroup isInEditMode={editMode} onModeToggle={setEditMode} canPreview={areRequiredFieldsPopulated()} />
        )}
      </div>
      {editMode
        ? (
          <DatasetFieldsEditor
            block={block}
            datasetOptions={dataSetOptions.current}
            isDatasetKeyValid={dataKeyStatus.isValid}
            handleDataSetChange={handleDataSetChange}
            dataFields={dataFields}
            dataFieldOptions={dataFieldOptions}
            handleDataFieldChange={handleDataFieldChange}
            subgroupColumns={subgroupColumns}
            showSplitChartOption
          />
        )
        // Preview
        : (
          <ReportBlock
            blockKey={block.key}
            report={pageEditorContext.page}
            isPreviewMode={true}
            {...block}
          />
        )
      }
    </div>
  )
}
